import { useState, useEffect, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { selectCurrentApplication } from '../../store/slices/application.slice';
import MainLayout from '../../components/layout/MainLayout';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { databaseApi, MonitoredDatabase, SlowQuery } from '../../services/api/database.api';
import api from '../../services/api/api.service';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import TimeRangeSelector, { TimeRange } from '../../components/common/TimeRangeSelector';

export default function DatabasePage() {
  const { t } = useTranslation(['database', 'common']);
  const navigate = useNavigate();
  const selectedAppId = useAppSelector(selectCurrentApplication);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [databases, setDatabases] = useState<MonitoredDatabase[]>([]);
  const [slowQueries, setSlowQueries] = useState<SlowQuery[]>([]);
  const [topSlowQueries, setTopSlowQueries] = useState<any[]>([]);
  const [selectedQuery, setSelectedQuery] = useState<string | null>(null);
  const [selectedDb, setSelectedDb] = useState<string | null>(null);
  const [connectionPoolData, setConnectionPoolData] = useState<any[]>([]);
  const [queryPerformanceData, setQueryPerformanceData] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedQueryDetails, setSelectedQueryDetails] = useState<any>(null);
  const [timeRange, setTimeRange] = useState<TimeRange>({
    startTime: Date.now() - 24 * 60 * 60 * 1000, // Last 24 hours
    endTime: Date.now(),
    rangeType: 'preset',
    presetValue: '24h',
  });

  useEffect(() => {
    fetchData();
  }, [selectedAppId, currentPage, timeRange]);

  useEffect(() => {
    if (selectedDb && Array.isArray(databases) && databases.length > 0) {
      fetchDbMetrics(selectedDb);
    }
  }, [selectedDb, databases, timeRange]);

  const getTimeRangeLabel = () => {
    if (timeRange.rangeType === 'preset') {
      const labels: Record<string, string> = {
        '15m': t('common:last15Minutes', 'Last 15 minutes'),
        '1h': t('common:last1Hour', 'Last hour'),
        '6h': t('common:last6Hours', 'Last 6 hours'),
        '24h': t('common:last24Hours', 'Last 24 hours'),
        '7d': t('common:last7Days', 'Last 7 days'),
        '30d': t('common:last30Days', 'Last 30 days'),
      };
      return labels[timeRange.presetValue || '24h'] || 'Last 24 hours';
    }
    return `${new Date(timeRange.startTime).toLocaleDateString()} - ${new Date(timeRange.endTime).toLocaleDateString()}`;
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const appId = selectedAppId || 'all';

      // Run ALL queries in PARALLEL for faster loading
      const [topQueriesResult, allQueriesResult, databasesResult] = await Promise.allSettled([
        // Fetch TOP 10 slowest queries with percentiles
        api.get('/v1/databases/query-stats', {
          params: {
            applicationId: appId,
            startTime: timeRange.startTime,
            endTime: timeRange.endTime,
            limit: 10,
            page: 1,
          },
        }),
        // Fetch ALL queries (paginated)
        api.get('/v1/apm/database/queries', {
          params: {
            applicationId: appId,
            startTime: timeRange.startTime,
            endTime: timeRange.endTime,
            limit: 20,
            offset: (currentPage - 1) * 20,
          },
        }),
        // Fetch databases
        databaseApi.getDatabases(appId),
      ]);

      // Process top queries result
      if (topQueriesResult.status === 'fulfilled') {
        const response = topQueriesResult.value;
        if (response.success && Array.isArray(response.data)) {
          setTopSlowQueries(response.data);
        } else {
          setTopSlowQueries([]);
        }
      } else {
        console.error('Failed to fetch top slow queries:', topQueriesResult.reason);
        setTopSlowQueries([]);
      }

      // Process all queries result
      if (allQueriesResult.status === 'fulfilled') {
        const response = allQueriesResult.value;
        if (response.success && Array.isArray(response.data)) {
          setSlowQueries(response.data);
          if (response.meta) {
            setTotalPages(Math.ceil(response.meta.total / 20));
          }
        } else {
          setSlowQueries([]);
        }
      } else {
        console.error('Failed to fetch query statistics:', allQueriesResult.reason);
        setSlowQueries([]);
      }

      // Process databases result
      if (databasesResult.status === 'fulfilled') {
        const dbsData = databasesResult.value;
        setDatabases(Array.isArray(dbsData) ? dbsData : []);
        if (Array.isArray(dbsData) && dbsData.length > 0) {
          setSelectedDb(dbsData[0].id);
        }
      } else {
        setDatabases([]);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load database data');
      console.error('Database fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDbMetrics = async (dbId: string) => {
    try {
      const appId = selectedAppId || 'all';

      const [volumeData, perfData] = await Promise.all([
        api.get(`/v1/databases/${dbId}/query-volume`, {
          params: {
            applicationId: appId,
            startTime: timeRange.startTime,
            endTime: timeRange.endTime,
          },
        }),
        api.get(`/v1/databases/${dbId}/query-performance`, {
          params: {
            applicationId: appId,
            startTime: timeRange.startTime,
            endTime: timeRange.endTime,
          },
        })
      ]);

      setConnectionPoolData(Array.isArray(volumeData) ? volumeData : []);
      setQueryPerformanceData(Array.isArray(perfData) ? perfData : []);
    } catch (err: any) {
      console.error('Failed to fetch database metrics:', err);
      setConnectionPoolData([]);
      setQueryPerformanceData([]);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-600 dark:text-gray-400">Loading database monitoring...</div>
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <div className="rounded-lg bg-red-50 dark:bg-red-900/20 p-4">
          <p className="text-red-800 dark:text-red-400">Error: {error}</p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('database:title')}</h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">{t('database:subtitle')}</p>

        {/* Time Range Filter */}
        <div className="mt-4 flex items-center gap-4">
          <TimeRangeSelector value={timeRange} onChange={setTimeRange} />
        </div>

        {/* Database Filter */}
        {Array.isArray(databases) && databases.length > 1 && (
          <div className="mt-4 w-full sm:w-64">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Select Database
            </label>
            <select
              value={selectedDb || ''}
              onChange={(e) => setSelectedDb(e.target.value)}
              className="block w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
            >
              {databases.map((db) => (
                <option key={db.id} value={db.id}>
                  {db.name} ({db.db_type})
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Database Stats */}
      {!Array.isArray(databases) || databases.length === 0 ? (
        <div className="mb-8 rounded-xl border border-indigo-200 bg-gradient-to-br from-indigo-50 to-white p-8 shadow-sm dark:border-indigo-900/50 dark:from-indigo-950/20 dark:to-gray-900">
          <div className="mx-auto max-w-3xl">
            {/* Icon */}
            <div className="mb-6 flex justify-center">
              <div className="rounded-full bg-indigo-100 p-3 dark:bg-indigo-900/30">
                <svg className="h-8 w-8 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
                </svg>
              </div>
            </div>

            {/* Title */}
            <h3 className="mb-2 text-center text-xl font-semibold text-gray-900 dark:text-white">
              {t('database:setup.title')}
            </h3>

            {/* Description */}
            <p className="mb-6 text-center text-gray-600 dark:text-gray-400">
              {t('database:setup.description')}
            </p>

            {/* How it works */}
            <div className="mb-6 rounded-lg border border-indigo-100 bg-white p-6 dark:border-indigo-900/30 dark:bg-gray-800/50">
              <h4 className="mb-4 font-semibold text-gray-900 dark:text-white">
                {t('database:setup.howItWorks')}
              </h4>
              <ol className="space-y-3 text-sm text-gray-700 dark:text-gray-300">
                <li className="flex items-start">
                  <span className="mr-3 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-indigo-100 text-xs font-semibold text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400">
                    1
                  </span>
                  <span className="mt-0.5">{t('database:setup.step1')}</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-3 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-indigo-100 text-xs font-semibold text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400">
                    2
                  </span>
                  <span className="mt-0.5">{t('database:setup.step2')}</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-3 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-indigo-100 text-xs font-semibold text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400">
                    3
                  </span>
                  <span className="mt-0.5">{t('database:setup.step3')}</span>
                </li>
              </ol>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col justify-center gap-3 sm:flex-row">
              <button
                onClick={() => navigate('/agents/apm/install')}
                className="inline-flex items-center justify-center rounded-lg bg-indigo-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition-all hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                <svg className="mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                {t('database:setup.setupAgent')}
              </button>
              <button
                onClick={() => navigate('/apm/traces')}
                className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-6 py-3 text-sm font-semibold text-gray-700 shadow-sm transition-all hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                <svg className="mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                {t('database:setup.viewApm')}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {databases.map((db) => (
            <div
              key={db.id}
              className={`rounded-xl border p-6 shadow-sm cursor-pointer transition-all ${
                selectedDb === db.id
                  ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                  : 'border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900'
              }`}
              onClick={() => setSelectedDb(db.id)}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900 dark:text-white truncate">{db.name}</h3>
                <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0 ml-2">{db.db_type || db.type}</span>
              </div>
              {db.application_name && (
                <div className="mb-3 flex items-center">
                  <svg className="mr-1.5 h-3.5 w-3.5 text-indigo-500 dark:text-indigo-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                  <span className="text-xs text-indigo-600 dark:text-indigo-400 font-medium truncate">{db.application_name}</span>
                </div>
              )}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Status</span>
                  <span className={`font-semibold ${db.status === 'online' || db.status === 'active' ? 'text-green-600 dark:text-green-400' : db.status === 'degraded' ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400'}`}>
                    {db.status}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Host</span>
                  <span className="font-semibold text-gray-900 dark:text-white truncate ml-2">{db.host}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Port</span>
                  <span className="font-semibold text-gray-900 dark:text-white">{db.port}</span>
                </div>
                {(db.active_connections !== undefined || db.connection_count !== undefined) && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Connections</span>
                    <span className="font-semibold text-gray-900 dark:text-white">
                      {db.active_connections || db.connection_count || 0}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Top 10 Slowest Queries */}
      {topSlowQueries.length > 0 && (
        <div className="mb-8">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{t('database:topSlowQueries.title', '🐌 Top 10 Slowest Queries')}</h2>
            <span className="text-sm text-gray-500 dark:text-gray-400">{getTimeRangeLabel()}</span>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {topSlowQueries.map((query, index) => (
              <div
                key={query.id}
                onClick={() => {
                  setSelectedQueryDetails(query);
                  setShowDetailsModal(true);
                }}
                className={`relative overflow-hidden rounded-xl border p-4 shadow-sm transition-all hover:shadow-lg cursor-pointer ${
                  query.avg_time > 1000
                    ? 'border-red-300 bg-red-50 dark:border-red-900/50 dark:bg-red-950/20 hover:border-red-400'
                    : query.avg_time > 500
                    ? 'border-yellow-300 bg-yellow-50 dark:border-yellow-900/50 dark:bg-yellow-950/20 hover:border-yellow-400'
                    : 'border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900 hover:border-gray-300'
                }`}
              >
                {/* Rank badge */}
                <div className="absolute -right-2 -top-2 h-12 w-12">
                  <div className={`flex h-full w-full items-center justify-center rounded-full text-xs font-bold text-white ${
                    index === 0 ? 'bg-red-500' : index === 1 ? 'bg-orange-500' : index === 2 ? 'bg-yellow-500' : 'bg-gray-400'
                  }`}>
                    #{index + 1}
                  </div>
                </div>

                <div className="pr-8">
                  <div className="mb-2 flex items-center gap-2">
                    <span className="inline-flex items-center rounded-md bg-indigo-100 px-2 py-1 text-xs font-medium text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400">
                      {query.db_system || 'database'}
                    </span>
                    {query.operation && (
                      <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                        {query.operation}
                      </span>
                    )}
                  </div>

                  <h3 className="mb-3 text-sm font-mono font-semibold text-gray-900 dark:text-white line-clamp-2">
                    {query.query_text}
                  </h3>

                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('database:topSlowQueries.avgTime')}:</span>
                      <span className={`font-bold ${
                        query.avg_time > 1000 ? 'text-red-600 dark:text-red-400' :
                        query.avg_time > 500 ? 'text-yellow-600 dark:text-yellow-400' :
                        'text-green-600 dark:text-green-400'
                      }`}>
                        {query.avg_time}ms
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('database:topSlowQueries.p95')}:</span>
                      <span className="font-semibold text-gray-900 dark:text-white">{query.p95}ms</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('database:topSlowQueries.p99')}:</span>
                      <span className="font-semibold text-gray-900 dark:text-white">{query.p99}ms</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('database:topSlowQueries.executions')}:</span>
                      <span className="font-semibold text-gray-900 dark:text-white">{query.execution_count.toLocaleString()}</span>
                    </div>
                    {query.error_count > 0 && (
                      <div className="flex justify-between text-red-600 dark:text-red-400">
                        <span>{t('database:topSlowQueries.errors')}:</span>
                        <span className="font-bold">{query.error_count} ({query.error_rate}%)</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Charts Grid */}
      <div className="mb-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Query Volume */}
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <h3 className="mb-6 text-lg font-semibold text-gray-900 dark:text-white">{t('database:connectionPool.title')}</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={connectionPoolData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.1} />
              <XAxis dataKey="time" stroke="#9CA3AF" />
              <YAxis stroke="#9CA3AF" />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1F2937',
                  border: 'none',
                  borderRadius: '0.5rem',
                  color: '#F9FAFB',
                }}
              />
              <Legend />
              <Line type="monotone" dataKey="total" stroke="#6366F1" strokeWidth={2} name={t('database:connectionPool.total')} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Query Performance */}
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <h3 className="mb-6 text-lg font-semibold text-gray-900 dark:text-white">{t('database:queryPerformance.title')}</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={queryPerformanceData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.1} />
              <XAxis dataKey="time" stroke="#9CA3AF" />
              <YAxis stroke="#9CA3AF" />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1F2937',
                  border: 'none',
                  borderRadius: '0.5rem',
                  color: '#F9FAFB',
                }}
              />
              <Legend />
              <Line type="monotone" dataKey="avgTime" stroke="#6366F1" strokeWidth={2} name={t('database:queryPerformance.avg')} />
              <Line type="monotone" dataKey="p95" stroke="#F59E0B" strokeWidth={2} name="P95" />
              <Line type="monotone" dataKey="p99" stroke="#EF4444" strokeWidth={2} name="P99" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Database Queries */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
        <div className="border-b border-gray-200 px-6 py-4 dark:border-gray-800">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('database:queries.title')}</h3>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                {t('database:queries.subtitle')}
              </p>
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {t('common:page')} {currentPage} {t('common:of')} {totalPages}
            </div>
          </div>
        </div>
        <div className="divide-y divide-gray-200 dark:divide-gray-800">
          {!Array.isArray(slowQueries) || slowQueries.length === 0 ? (
            <div className="p-6 text-center text-gray-500 dark:text-gray-400">
              {t('database:queries.noQueries')}
            </div>
          ) : (
            slowQueries.map((query) => {
              const isSlow = query.severity === 'critical' || query.severity === 'high';
              const borderColor = isSlow ? 'border-l-4 border-l-red-500' : 'border-l-4 border-l-transparent';

              return (
                <div
                  key={query.id}
                  className={`cursor-pointer p-6 transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/50 ${borderColor}`}
                  onClick={() => setSelectedQuery(selectedQuery === query.id ? null : query.id)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mb-2">
                        <div className="flex items-center space-x-2">
                          <span
                            className={`inline-flex h-2 w-2 rounded-full ${
                              isSlow ? 'bg-red-500 animate-pulse' : 'bg-yellow-500'
                            }`}
                          ></span>
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                            {query.query_type || 'QUERY'} • {query.table_name || 'unknown'}
                          </span>
                          {query.db_system && (
                            <span className="inline-flex items-center rounded-md bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-700/10 dark:bg-indigo-900/20 dark:text-indigo-400">
                              {query.db_system}
                            </span>
                          )}
                          {isSlow && (
                            <span className="inline-flex items-center rounded-md bg-red-50 px-2 py-1 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/10 dark:bg-red-900/20 dark:text-red-400">
                              SLOW
                            </span>
                          )}
                        </div>
                        {query.application_name && (
                          <div className="flex items-center">
                            <svg className="mr-1 h-3 w-3 text-indigo-500 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                            </svg>
                            <span className="text-xs font-medium text-indigo-600 dark:text-indigo-400">
                              {query.application_name}
                            </span>
                          </div>
                        )}
                      </div>
                    <code className="block text-sm text-gray-900 dark:text-white font-mono break-all">
                      {query.query_text}
                    </code>
                    <div className="mt-3 flex items-center flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600 dark:text-gray-400">
                      <span>{t('database:slowQueries.executions')}: {query.execution_count?.toLocaleString() || 0}</span>
                      <span>•</span>
                      <span>Last: {new Date(query.last_execution || query.detected_at).toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="ml-4 text-right">
                    <div
                      className={`text-2xl font-bold ${
                        (query.avg_execution_time ?? 0) > 2000
                          ? 'text-red-600 dark:text-red-400'
                          : (query.avg_execution_time ?? 0) > 1000
                          ? 'text-yellow-600 dark:text-yellow-400'
                          : 'text-green-600 dark:text-green-400'
                      }`}
                    >
                      {(query.avg_execution_time ?? 0).toFixed(0)}ms
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{t('database:stats.avgTime')}</div>
                  </div>
                </div>

                {selectedQuery === query.id && (
                  <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
                    <h4 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">
                      {t('database:queries.details')}
                    </h4>
                    <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                      <div className="flex justify-between">
                        <span>{t('database:queries.maxTime')}:</span>
                        <span className="font-semibold">{query.max_execution_time?.toFixed(0)}ms</span>
                      </div>
                      <div className="flex justify-between">
                        <span>{t('database:queries.minTime')}:</span>
                        <span className="font-semibold">{query.min_execution_time?.toFixed(0)}ms</span>
                      </div>
                      {query.query_plan && (
                        <div className="mt-3">
                          <span className="font-semibold">{t('database:queries.queryPlan')}:</span>
                          <pre className="mt-1 p-2 bg-gray-800 rounded text-xs overflow-x-auto">
                            {JSON.stringify(query.query_plan, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
            })
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="border-t border-gray-200 px-6 py-4 dark:border-gray-800">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700 dark:text-gray-300">
                {t('common:pagination', 'Page {{current}} of {{total}}', { current: currentPage, total: totalPages })}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  {t('common:previous')}
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  {t('common:next')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Query Details Modal */}
      <Transition appear show={showDetailsModal} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowDetailsModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/50" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white dark:bg-gray-900 p-6 shadow-xl transition-all">
                  <div className="flex items-start justify-between mb-4">
                    <Dialog.Title className="text-2xl font-bold text-gray-900 dark:text-white">
                      {t('database:queries.details')}
                    </Dialog.Title>
                    <button
                      onClick={() => setShowDetailsModal(false)}
                      className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-500"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>

                  {selectedQueryDetails && (
                    <div className="space-y-6">
                      {/* Query Info Header */}
                      <div className="flex items-center gap-3">
                        <span className="inline-flex items-center rounded-md bg-indigo-100 px-3 py-1.5 text-sm font-medium text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400">
                          {selectedQueryDetails.db_system || 'database'}
                        </span>
                        {selectedQueryDetails.operation && (
                          <span className="inline-flex items-center rounded-md bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                            {selectedQueryDetails.operation}
                          </span>
                        )}
                        {selectedQueryDetails.db_name && (
                          <span className="inline-flex items-center rounded-md bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                            📊 {selectedQueryDetails.db_name}
                          </span>
                        )}
                      </div>

                      {/* Query Text */}
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Query</h3>
                        <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-4 font-mono text-sm text-gray-900 dark:text-gray-100 overflow-x-auto">
                          {selectedQueryDetails.query_text}
                        </div>
                      </div>

                      {/* Full Statement if available */}
                      {selectedQueryDetails.full_statement && selectedQueryDetails.full_statement !== selectedQueryDetails.query_text && (
                        <div>
                          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Full Statement</h3>
                          <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-4 font-mono text-xs text-gray-900 dark:text-gray-100 overflow-x-auto max-h-60 overflow-y-auto">
                            {selectedQueryDetails.full_statement}
                          </div>
                        </div>
                      )}

                      {/* Performance Metrics */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="rounded-lg border border-gray-200 dark:border-gray-800 p-4">
                          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('database:topSlowQueries.avgTime')}</div>
                          <div className={`text-2xl font-bold ${
                            selectedQueryDetails.avg_time > 1000 ? 'text-red-600 dark:text-red-400' :
                            selectedQueryDetails.avg_time > 500 ? 'text-yellow-600 dark:text-yellow-400' :
                            'text-green-600 dark:text-green-400'
                          }`}>
                            {selectedQueryDetails.avg_time}ms
                          </div>
                        </div>

                        <div className="rounded-lg border border-gray-200 dark:border-gray-800 p-4">
                          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('database:queries.minTime')}</div>
                          <div className="text-2xl font-bold text-gray-900 dark:text-white">
                            {selectedQueryDetails.min_time}ms
                          </div>
                        </div>

                        <div className="rounded-lg border border-gray-200 dark:border-gray-800 p-4">
                          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('database:queries.maxTime')}</div>
                          <div className="text-2xl font-bold text-gray-900 dark:text-white">
                            {selectedQueryDetails.max_time}ms
                          </div>
                        </div>

                        <div className="rounded-lg border border-gray-200 dark:border-gray-800 p-4">
                          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('database:topSlowQueries.executions')}</div>
                          <div className="text-2xl font-bold text-gray-900 dark:text-white">
                            {selectedQueryDetails.execution_count.toLocaleString()}
                          </div>
                        </div>
                      </div>

                      {/* Percentiles */}
                      <div className="grid grid-cols-3 gap-4">
                        <div className="rounded-lg border border-indigo-200 dark:border-indigo-900 bg-indigo-50 dark:bg-indigo-950/20 p-4">
                          <div className="text-xs text-indigo-600 dark:text-indigo-400 mb-1">P50 (Median)</div>
                          <div className="text-xl font-bold text-indigo-900 dark:text-indigo-300">
                            {selectedQueryDetails.p50}ms
                          </div>
                        </div>

                        <div className="rounded-lg border border-orange-200 dark:border-orange-900 bg-orange-50 dark:bg-orange-950/20 p-4">
                          <div className="text-xs text-orange-600 dark:text-orange-400 mb-1">{t('database:topSlowQueries.p95')}</div>
                          <div className="text-xl font-bold text-orange-900 dark:text-orange-300">
                            {selectedQueryDetails.p95}ms
                          </div>
                        </div>

                        <div className="rounded-lg border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/20 p-4">
                          <div className="text-xs text-red-600 dark:text-red-400 mb-1">{t('database:topSlowQueries.p99')}</div>
                          <div className="text-xl font-bold text-red-900 dark:text-red-300">
                            {selectedQueryDetails.p99}ms
                          </div>
                        </div>
                      </div>

                      {/* Errors */}
                      {selectedQueryDetails.error_count > 0 && (
                        <div className="rounded-lg border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/20 p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-sm font-semibold text-red-900 dark:text-red-300">
                              ⚠️ {t('database:topSlowQueries.errors')}
                            </span>
                          </div>
                          <div className="flex gap-6">
                            <div>
                              <div className="text-xs text-red-600 dark:text-red-400">Total Errors</div>
                              <div className="text-2xl font-bold text-red-900 dark:text-red-300">
                                {selectedQueryDetails.error_count}
                              </div>
                            </div>
                            <div>
                              <div className="text-xs text-red-600 dark:text-red-400">Error Rate</div>
                              <div className="text-2xl font-bold text-red-900 dark:text-red-300">
                                {selectedQueryDetails.error_rate}%
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Optimization Recommendations */}
                      {selectedQueryDetails.avg_time > 500 && (
                        <div className="rounded-lg border border-yellow-200 dark:border-yellow-900 bg-yellow-50 dark:bg-yellow-950/20 p-4">
                          <h3 className="text-sm font-semibold text-yellow-900 dark:text-yellow-300 mb-3">
                            💡 {t('database:slowQueries.recommendations')}
                          </h3>
                          <ul className="space-y-2 text-sm text-yellow-800 dark:text-yellow-200">
                            <li className="flex items-start gap-2">
                              <span>•</span>
                              <span>{t('database:slowQueries.addIndex')}</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span>•</span>
                              <span>{t('database:slowQueries.optimizeJoin')}</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span>•</span>
                              <span>{t('database:slowQueries.addCache')}</span>
                            </li>
                          </ul>
                        </div>
                      )}

                      {/* Last Execution */}
                      {selectedQueryDetails.last_execution && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {t('database:slowQueries.lastExecution')}: {new Date(selectedQueryDetails.last_execution).toLocaleString()}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="mt-6 flex justify-end">
                    <button
                      onClick={() => setShowDetailsModal(false)}
                      className="rounded-lg bg-indigo-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      {t('common:close')}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </MainLayout>
  );
}
